import {Component, ElementRef, ViewChild} from '@angular/core';
import {BehaviorSubject, combineLatest, Subscription} from "rxjs";
import ContextBarService from "../context-bar.service";
import DoingModel from "../../../../model/doing.model";
import {debounceTime} from "rxjs/operators";
import DoingService from "../../../../api/doing/doing.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import NoteModel from "../../../../model/note.model";
import dayjs from "dayjs";
import addBusinessDays from "../../../util/addBusinessDays";

@Component({
  selector: 'context-bar-doings',
  templateUrl: './doings.component.html',
  styleUrls: ['./doings.component.scss']
})
export class DoingsComponent {
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  private doingSubtypeSubject = new BehaviorSubject<'all'|'event'|'management'>('all');
  public selectedDoingSubtype$  = this.doingSubtypeSubject.asObservable();

  private querySubject = new BehaviorSubject<string>(null);
  public query$ = this.querySubject.asObservable();

  private doingSortSubject = new BehaviorSubject<{order: string, name: string}>({ order: 'desc', name: 'created' });
  public doingSort$ = this.doingSortSubject.asObservable();

  private doingsSubject = new BehaviorSubject<DoingModel[]>([]);
  public doings$ = this.doingsSubject.asObservable();

  private doingsPageSubject = new BehaviorSubject<number>(1);
  public doingsPage$ = this.doingsPageSubject.asObservable();

  public doingCount = 0;

  private overdueLoadingSubject = new BehaviorSubject<boolean>(false);
  public overdueLoading$ = this.overdueLoadingSubject.asObservable();

  private overdueDoingsSubject = new BehaviorSubject<DoingModel[]>([]);
  public overdueDoings$ = this.overdueDoingsSubject.asObservable();

  private overdueDoingsPageSubject = new BehaviorSubject<number>(1);
  private overdueDoingsPage$ = this.overdueDoingsPageSubject.asObservable();

  public overdueDoingCount = 0;

  private showArchivedDoingsSubject = new BehaviorSubject<boolean>(false);
  public showArchivedDoings$ = this.showArchivedDoingsSubject.asObservable();

  public newDoing: DoingModel;

  private doingSubscription: Subscription;
  private overdueSubscription: Subscription;

  @ViewChild('doingSearchInput', { read: ElementRef }) doingSearchInput: ElementRef;

  public constructor(private contextBarService: ContextBarService, private doingService: DoingService, private snackBar: MatSnackBar) {
    let followUp = addBusinessDays(new Date(), 3);
    followUp.setHours(8, 0, 0);
    this.newDoing = {
      text: '',
      state: 'new',
      followUp: followUp
    };

    if(localStorage.getItem('doing_subtype')) {
      this.doingSubtypeSubject.next(localStorage.getItem('doing_subtype') as any);
    }


    combineLatest([
        this.contextBarService.artist$,
        this.contextBarService.cooperation$,
        this.contextBarService.customer$,
    ]).subscribe(([artist, cooperation, customer]) => {
      this.newDoing.customer = customer;
      this.newDoing.cooperation = cooperation;
      this.newDoing.markedUser = artist?.user;
      this.querySubject.next('');
      if(this.doingSearchInput?.nativeElement) {
        this.doingSearchInput.nativeElement.value = '';
      }
    });

    combineLatest([ this.contextBarService.artist$, this.contextBarService.cooperation$, this.contextBarService.customer$, this.query$]).subscribe(_ => {
      this.doingsPageSubject.next(1);
      this.overdueDoingsPageSubject.next(1);
    });


    combineLatest([
      this.contextBarService.artist$,
      this.contextBarService.cooperation$,
      this.contextBarService.customer$,
      this.selectedDoingSubtype$,
      this.doingSort$,
      this.showArchivedDoings$,
      this.query$,
      this.doingsPage$
    ]).pipe(debounceTime(200)).subscribe(([
        artist, cooperation, customer, subtype, sort, archived, query, page
    ]) => {
      let additional = {
        overdue_only: false
      };

      if(artist) {
        additional['artist'] = artist.id;
      }
      if(cooperation) {
        additional['cooperation'] = cooperation.id;
      }
      if(customer) {
        additional['customer'] = customer.id;
      }
      if(subtype) {
        additional['subtype'] = subtype;
      }
      if(archived) {
        additional['archived'] = 1;
      }

      this.loadingSubject.next(true);
      if(this.doingSubscription) { this.doingSubscription.unsubscribe(); }
      this.doingSubscription = this.doingService.list(query, page, 30, sort?.name, sort?.order, additional).subscribe(result => {
        if(result.page == 1) {
          this.doingsSubject.next(result.results);
        } else {
          let items = this.doingsSubject.getValue();
          items = items.concat(result.results);
          this.doingsSubject.next(items);
        }
        if(!result.cachedVersion) {
          this.loadingSubject.next(false);
        }

        this.doingCount = result.total;
      });
    });


    combineLatest([
      this.contextBarService.artist$,
      this.contextBarService.cooperation$,
      this.contextBarService.customer$,
      this.selectedDoingSubtype$,
      this.doingSort$,
      this.showArchivedDoings$,
      this.query$,
      this.overdueDoingsPage$
    ]).pipe(debounceTime(500)).subscribe(([
                                            artist, cooperation, customer, subtype, sort, archived, query, page
                                          ]) => {
      let additional = {
        overdue_only: true
      };
      if(artist) {
        additional['artist'] = artist.id;
      }
      if(cooperation) {
        additional['cooperation'] = cooperation.id;
      }
      if(customer) {
        additional['customer'] = customer.id;
      }
      localStorage.setItem('doing_subtype', subtype);
      if(subtype) {
        additional['subtype'] = subtype;
      }
      if(archived) {
        additional['archived'] = 1;
      }

      this.overdueLoadingSubject.next(true);

      if(this.overdueSubscription) { this.overdueSubscription.unsubscribe(); }
      this.overdueSubscription = this.doingService.list(query, page, 30, sort?.name, sort?.order, additional).subscribe(result => {
        if(result.page == 1) {
          this.overdueDoingsSubject.next(result.results);
        } else {
          let items = this.overdueDoingsSubject.getValue();
          items = items.concat(result.results);
          this.overdueDoingsSubject.next(items);
        }
        if(!result.cachedVersion) {
          this.overdueLoadingSubject.next(false);
        }
        this.overdueDoingCount = result.total;
      });
    });
  }

  public moveOverdueToNormal(doing: DoingModel) {
    const overdues = this.overdueDoingsSubject.getValue();
    let overdueIndex = overdues.indexOf(doing);
    if(overdueIndex >= 0) {
      overdues.splice(overdueIndex, 1);
      this.overdueDoingsSubject.next(overdues);

      const normalDoings = this.doingsSubject.getValue();
      normalDoings.unshift(doing);
      this.doingsSubject.next(normalDoings);

      this.overdueDoingCount--;
      this.doingCount++;
    }

  }

  queryChanged(query) {
    this.querySubject.next(query);
  }

  changeShowArchivedDoings(event) {
    if(event) {
      this.doingSortSubject.next({ name: 'created', order: 'desc' });
    }
    this.showArchivedDoingsSubject.next(event);
  }

  identify(index, item) {
    return item.id;
  }

  public onDoingScroll() {
    if(this.doingsSubject.getValue().length < this.doingCount) {
      this.doingsPageSubject.next(this.doingsPageSubject.getValue() + 1);
    }
  }

  public onOverdueDoingScroll() {
    if(this.overdueDoingsSubject.getValue().length < this.overdueDoingCount) {
      this.overdueDoingsPageSubject.next(this.overdueDoingsPageSubject.getValue() + 1);
    }
  }

  public addDoing(doing: DoingModel) {
    if(this.doingSubtypeSubject.getValue() != 'all') {
      doing.subtype = this.doingSubtypeSubject.getValue() as any;
    }

    if (this.contextBarService.getCooperationSnapshot() && doing.cooperation == null) {
      let valid = true;
      if(doing.customer && doing.customer.id != this.contextBarService.getCooperationSnapshot().customer.id) {
        valid = false;
      }
      if(doing.markedUser && doing.markedUser.id != this.contextBarService.getCooperationSnapshot().artist.user.id) {
        valid = false;
      }
      if (valid) {
        doing.cooperation = this.contextBarService.getCooperationSnapshot();
        doing.markedUser = this.contextBarService.getCooperationSnapshot().artist.user;
      }
    } else if (this.contextBarService.getArtistSnapshot() && this.contextBarService.getArtistSnapshot().user && doing.markedUser == null) {
      doing.markedUser = this.contextBarService.getArtistSnapshot().user;
    }

    this.doingService.create(doing).subscribe(result => {
      const doings = this.doingsSubject.getValue();
      doings.push(result);
      this.doingsSubject.next(doings);
      this.snackBar.open('Doing hinzugefügt');
    }, error => {
      this.snackBar.open('Fehler beim Hinzufügen des Doings');
      Object.assign(this.newDoing, doing);
    });
  }

  public doingDeleted(doing: DoingModel) {
    const doings = this.doingsSubject.getValue();
    const index = doings.indexOf(doing);
    if (index >= 0 && doing.predecessor == null) {
      doings.splice(index, 1);
    }
    this.doingsSubject.next(doings);
  }

  public changeSubtype(type) {
    this.doingSubtypeSubject.next(type);
    localStorage.setItem('doing_subtype',type);
  }
}
